import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaBlog,
  FaUser,
  FaSignInAlt,
  FaSignOutAlt,
  FaUserPlus,
  FaSearch,
  FaComments,
  FaEnvelope,
  FaChevronDown,
  FaPhoneSquare,
  FaClipboardList,
  FaExclamationTriangle,
  FaEllipsisH,
  FaInfoCircle,
  FaBoxOpen,
  FaPoundSign,
  FaHardHat,
  FaToolbox,
} from "react-icons/fa";
import useAuth from "../hooks/useAuth";
import useLogout from "../hooks/useLogout";
import useUnreadMessages from "../hooks/useUnreadMessages";
import "../styles/Toolbar.css";
import "../styles/Messages.css";

const Toolbar = () => {
  const { user } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const mobileUserMenuRef = useRef(null);
  const mobileMoreMenuRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (![userMenuRef, mobileUserMenuRef].some(ref => ref.current?.contains(event.target))) {
        setIsUserMenuOpen(false);
      }
      if (![moreMenuRef, mobileMoreMenuRef].some(ref => ref.current?.contains(event.target))) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      setIsMobileMenuOpen(false);
      setIsUserMenuOpen(false);
      setIsMoreMenuOpen(false);
      mobileMenuButtonRef.current?.focus();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    setIsUserMenuOpen(false);
    setIsMoreMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  async function onLogout() {
    await logout();
    setIsUserMenuOpen(false);
    navigate("/");
  }

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleDropdownNavigate = (path) => {
    navigate(path);
    setIsUserMenuOpen(false);
    setIsMoreMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleChatClick = () => {
    if (user && user.user_type === "contractor") {
        return;
    }
    navigate("/chat");
  };

  const handleManageProfileClick = () => {
    if (user.user_type === "contractor") {
      navigate("/contractor-profile");
    } else if (user.user_type === "homeowner") {
      navigate("/homeowner-profile");
    }
    setIsUserMenuOpen(false);
  };

  const handleNavigateHome = () => {
    if (user && user.user_type === "contractor") {
      navigate("/contractor-home");
    } else {
      navigate("/home");
    }
  };

  const renderButtons = (mobile = false) => {
    // Admin/sales/merchant/partner users use the AdminSidebar — this toolbar
    // is only rendered for public users, homeowners, and contractors.
    const isHomeowner = user && user.user_type === "homeowner";
    const isContractor = user && user.user_type === "contractor";

    return (
      <>
        <button
          onClick={handleNavigateHome}
          className="toolbar-button"
          disabled={
            location.pathname === "/" ||
            location.pathname === "/home" ||
            location.pathname === "/contractor-home"
          }
        >
          <FaHome className="toolbar-icon" /> Home
        </button>

        {(!user || !isContractor) && (
          <button
            onClick={() => handleNavigate("/local-contractors")}
            className="toolbar-button"
            disabled={
              location.pathname === "/local-contractors" ||
              location.pathname.startsWith("/contractor-search/")
            }
          >
            <FaSearch className="toolbar-icon" /> Contractors
          </button>
        )}

        {/* Estimate CTA - prominent button */}
        {(!user || !isContractor) && (
          <button
            onClick={handleChatClick}
            className="toolbar-button toolbar-chat-cta"
            disabled={location.pathname === "/chat"}
          >
            <FaComments className="toolbar-icon" /> Get Estimate
          </button>
        )}

        {/* More dropdown - consolidates secondary nav items */}
        {isHomeowner && (
          <div className="toolbar-dropdown" ref={mobile ? mobileMoreMenuRef : moreMenuRef}>
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className={`toolbar-button ${isMoreMenuOpen ? "active" : ""}`}
            >
              <FaEllipsisH className="toolbar-icon" /> More
              {unreadCount > 0 && (
                <span className="toolbar-msg-count">{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
              <FaChevronDown className="dropdown-arrow" />
            </button>
            {isMoreMenuOpen && (
              <div className="dropdown-menu">
                <button
                  onClick={() => handleDropdownNavigate("/home-health")}
                  className="dropdown-item"
                >
                  <FaClipboardList className="toolbar-icon" /> My Home
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/emergency")}
                  className="dropdown-item dropdown-item--emergency"
                >
                  <FaExclamationTriangle className="toolbar-icon" /> Emergency
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/messages")}
                  className="dropdown-item"
                >
                  <FaEnvelope className="toolbar-icon" /> Messages
                  {unreadCount > 0 && (
                    <span className="toolbar-msg-count">{unreadCount > 99 ? "99+" : unreadCount}</span>
                  )}
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/blog")}
                  className="dropdown-item"
                >
                  <FaBlog className="toolbar-icon" /> Blog
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/cost-guides")}
                  className="dropdown-item"
                >
                  <FaPoundSign className="toolbar-icon" /> Costs Guides
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/day-rates")}
                  className="dropdown-item"
                >
                  <FaHardHat className="toolbar-icon" /> Local Trades Rates
                </button>
                <a href="tel:01296325153" className="dropdown-item">
                  <FaPhoneSquare className="toolbar-icon" /> Call Us
                </a>
              </div>
            )}
          </div>
        )}

        {/* Messages button - contractors only (homeowners use the More dropdown) */}
        {isContractor && (
          <button
            onClick={() => handleNavigate("/messages")}
            className="toolbar-button"
            disabled={location.pathname.startsWith("/messages")}
            style={{ position: "relative" }}
          >
            <FaEnvelope className="toolbar-icon" /> Messages
            {unreadCount > 0 && (
              <span className="msg-toolbar-badge toolbar-messages-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
            )}
          </button>
        )}

        {/* Non-homeowner, non-contractor secondary items — consolidated into More dropdown */}
        {!user && (
          <div className="toolbar-dropdown" ref={mobile ? mobileMoreMenuRef : moreMenuRef}>
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className={`toolbar-button ${isMoreMenuOpen ? "active" : ""}`}
            >
              <FaEllipsisH className="toolbar-icon" /> More
              <FaChevronDown className="dropdown-arrow" />
            </button>
            {isMoreMenuOpen && (
              <div className="dropdown-menu">
                <button
                  onClick={() => handleDropdownNavigate("/learn-more")}
                  className="dropdown-item"
                >
                  <FaInfoCircle className="toolbar-icon" /> Learn More
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/contractor-packages?source=nav_more")}
                  className="dropdown-item"
                >
                  <FaBoxOpen className="toolbar-icon" /> Packages
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/blog")}
                  className="dropdown-item"
                >
                  <FaBlog className="toolbar-icon" /> Blog
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/cost-guides")}
                  className="dropdown-item"
                >
                  <FaPoundSign className="toolbar-icon" /> Costs Guides
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/day-rates")}
                  className="dropdown-item"
                >
                  <FaHardHat className="toolbar-icon" /> Local Trades Rates
                </button>
                <button
                  onClick={() => handleDropdownNavigate("/trade-advice")}
                  className="dropdown-item"
                >
                  <FaToolbox className="toolbar-icon" /> Trade Advice
                </button>
                <a href="tel:01296325153" className="dropdown-item">
                  <FaPhoneSquare className="toolbar-icon" /> Call Us
                </a>
              </div>
            )}
          </div>
        )}

        <div className="toolbar-dropdown" ref={mobile ? mobileUserMenuRef : userMenuRef}>
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className={`toolbar-button ${isUserMenuOpen ? "active" : ""}`}
          >
            <FaUser className="toolbar-icon" />
            Account
            <FaChevronDown className="dropdown-arrow" />
          </button>
          {isUserMenuOpen && (
            <div className="dropdown-menu">
              {user ? (
                <>
                  <button
                    onClick={handleManageProfileClick}
                    className="dropdown-item"
                  >
                    <FaUser className="toolbar-icon" /> Profile
                  </button>
                  <button onClick={onLogout} className="dropdown-item">
                    <FaSignOutAlt className="toolbar-icon" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleDropdownNavigate("/login")}
                    className="dropdown-item"
                  >
                    <FaSignInAlt className="toolbar-icon" /> Login
                  </button>
                  <button
                    onClick={() => handleDropdownNavigate("/signup")}
                    className="dropdown-item"
                  >
                    <FaUserPlus className="toolbar-icon" /> Sign Up
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="toolbar-container">
      <div className="toolbar-content">
        <button
          onClick={handleNavigateHome}
          className="toolbar-logo-button"
          aria-label="Return to home page"
        >
          <img
            className="toolbar-logo-image"
            src="/images/penguin-alone.png"
            alt="Penguin logo"
            width="40"
            height="40"
          />
          <span className="toolbar-logo-text">
            Pricing <span className="toolbar-logo-accent">Penguin</span>
          </span>
        </button>

        <div className="toolbar-desktop-menu">{renderButtons()}</div>

        <button
          type="button"
          className="toolbar-mobile-menu-button"
          ref={mobileMenuButtonRef}
          onClick={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen);
            setIsUserMenuOpen(false);
            setIsMoreMenuOpen(false);
          }}
          aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="toolbar-mobile-menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="toolbar-mobile-menu-icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={
                isMobileMenuOpen
                  ? "M6 18L18 6M6 6l12 12"
                  : "M4 6h16M4 12h16M4 18h16"
              }
            />
          </svg>
        </button>
      </div>

      <nav
        id="toolbar-mobile-menu"
        aria-label="Mobile navigation"
        hidden={!isMobileMenuOpen}
        className={`toolbar-mobile-menu ${
          isMobileMenuOpen ? "toolbar-mobile-menu-open" : ""
        }`}
      >
        {isMobileMenuOpen && <div className="toolbar-mobile-menu-items">{renderButtons(true)}</div>}
      </nav>
    </div>
  );
};
export default Toolbar;
